import { useCallback, useEffect, useState } from "react"

import type { HoverNavItem } from "@productlane/ui"
import { generateHeadlineKey } from "@productlane/lib/src/articles"
import { HoverNav } from "@productlane/ui"

import { scrollParent } from "@/lib/scroll-parent"
import { scrollWithinContainer } from "./scroll-within"

/** The heading elements the editor is currently showing, in document order. */
export function readEditorHeadings(root: ParentNode): HTMLElement[] {
	return Array.from(root.querySelectorAll<HTMLElement>("h1, h2, h3")).filter(
		(heading) => (heading.textContent ?? "").trim().length > 0,
	)
}

/** Nav items for those headings, keyed the way the portal keys them. */
export function headingsToItems(headings: HTMLElement[]): HoverNavItem[] {
	return headings.map((heading, index) => {
		const title = (heading.textContent ?? "").trim()
		return {
			key: generateHeadlineKey(title, index),
			title,
			level: Number(heading.tagName.slice(1)),
		}
	})
}

/** Nav items for stored article HTML, before the editor has rendered it. */
export function headingItemsFromHtml(html: string): HoverNavItem[] {
	if (!html || typeof DOMParser === "undefined") return []
	const parsed = new DOMParser().parseFromString(html, "text/html")
	return headingsToItems(readEditorHeadings(parsed.body))
}

/**
 * The portal's hover nav, over the docs editor. Headings come from the live
 * editor DOM rather than the saved HTML, so a heading typed a second ago is
 * already in the list.
 */
export function DocsHoverNav({
	editorKey,
	content,
	className,
}: {
	/** Changes when the editor remounts, e.g. on a language switch. */
	editorKey: string
	/** The current article HTML; re-reads the headings after every edit. */
	content: string
	className?: string
}) {
	// Seeded from the stored HTML so the nav, and the column it pushes, are in
	// place on the first paint instead of popping in a frame later.
	const [items, setItems] = useState(() => headingItemsFromHtml(content))

	useEffect(() => {
		// The editor writes its HTML a frame before the DOM settles.
		const frame = requestAnimationFrame(() => {
			const editor = document.querySelector(".ProseMirror")
			if (editor) setItems(headingsToItems(readEditorHeadings(editor)))
		})
		return () => cancelAnimationFrame(frame)
	}, [content, editorKey])

	const scrollToHeading = useCallback((key: string) => {
		const editor = document.querySelector(".ProseMirror")
		if (!editor) return
		const headings = readEditorHeadings(editor)
		const index = headingsToItems(headings).findIndex(
			(item) => item.key === key,
		)
		const target = headings[index]
		if (!target) return
		const container = scrollParent(target)
		if (container) scrollWithinContainer(container, target)
	}, [])

	return (
		<HoverNav
			items={items}
			onClick={scrollToHeading}
			className={className}
			indicatorClassName="bg-gray-900 dark:bg-gray-100"
		/>
	)
}
